import { useEffect, useState, useMemo } from 'react';
import { getAllPosts } from '../lib/blog';
import BlogCard from '../components/Blog/BlogCard';
import BlogSearch from '../components/Blog/BlogSearch';
import { BlogPost } from '../lib/blog';
import { Header } from '@/components/landing/Header';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils/date';
import { useTranslation } from '@/hooks/useTranslation';

export default function Blog() {
    const { t, i18n } = useTranslation();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Categories with counts - only show categories with posts
    const categories = [
        { id: 'all', name: t('blog.allPosts'), count: posts.length },
        { id: 'guides', name: t('blog.guidesAndTutorials'), count: posts.length },
    ];

    // Get featured post (most recent or specially marked)
    const featuredPost = useMemo(() => {
        return posts[0] || null;
    }, [posts]);

    // Get regular posts (all posts when not searching)
    const regularPosts = useMemo(() => {
        return posts;
    }, [posts]);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const allPosts = await getAllPosts(i18n.language);
                setPosts(allPosts);
                setFilteredPosts(allPosts);
            } catch (error) {
                console.error('Error fetching posts:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [i18n.language]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6547D9] mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('blog.loadingArticles')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fb-public min-h-screen w-full relative overflow-hidden bg-white">
            <a href="#main-content" className="fb-skip-link">
                {i18n.language?.startsWith('fr') ? 'Aller au contenu' : 'Skip to content'}
            </a>

            {/* Main content */}
            <main id="main-content">
                <div className="mx-auto max-w-7xl">
                    <Header />

                    <div className="w-full px-4 sm:px-6 lg:px-8 py-20">
                        {/* Enhanced Header content */}
                        <div className="text-center space-y-6">
                            {/* Main Title */}
                            <h1 className="text-5xl md:text-6xl font-bold">
                                {t('blog.theFeedbaskBlog')}
                            </h1>

                            {/* Enhanced subtitle */}
                            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                                {t('blog.discoverHowToBuild')}
                                <span className="block mt-2 text-lg text-gray-500">
                                    {t('blog.tipsStrategiesAndInsights')}
                                </span>
                            </p>

                            {/* Stats row */}
                            <div className="flex justify-center items-center gap-8 pt-4">
                                <div className="flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-600">{t('blog.articlesCount', { count: posts.length })}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-600">{t('blog.fiveMinAvgRead')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content section */}
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">

                    {/* Search and Articles */}
                    {posts.length > 0 ? (
                        <div>
                            {/* Featured Post Section */}
                            {featuredPost && (
                                <div className="mb-16">
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                        <span className="px-4 py-1 bg-[#6547D9] text-white text-sm font-medium rounded-full">{t('blog.featuredPost')}</span>
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                    </div>

                                    <Link to={`/blog/${featuredPost.slug}`} className="group block">
                                        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
                                            <div className="grid md:grid-cols-2 gap-8">
                                                {/* Featured Image */}
                                                <div className="h-64 md:h-full overflow-hidden">
                                                    <img
                                                        src={featuredPost.cover || "/images/blog/Whisk_7360f2eafe.jpg"}
                                                        alt={featuredPost.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>

                                                {/* Featured Content */}
                                                <div className="p-8 md:p-12 flex flex-col justify-center">
                                                    <div className="inline-flex items-center gap-2 text-sm text-[#6547D9] font-medium mb-4">
                                                        <span className="w-2 h-2 bg-[#6547D9] rounded-full"></span>
                                                        {t('blog.newPost')}
                                                    </div>

                                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 group-hover:text-[#6547D9] transition-colors duration-300">
                                                        {featuredPost.title}
                                                    </h2>

                                                    {featuredPost.excerpt && (
                                                        <p className="text-lg text-gray-600 mb-6 line-clamp-3">
                                                            {featuredPost.excerpt}
                                                        </p>
                                                    )}

                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src="https://unavatar.io/x/tarunyadav9761"
                                                                alt="Tarun Yadav"
                                                                className="w-10 h-10 rounded-full"
                                                            />
                                                            <div>
                                                                <p className="font-medium text-gray-900">Tarun Yadav</p>
                                                                <p className="text-sm text-gray-500">{formatDate(featuredPost.date)}</p>
                                                            </div>
                                                        </div>

                                                        <span className="inline-flex items-center gap-2 text-[#6547D9] font-medium group-hover:gap-3 transition-all duration-300">
                                                            {t('blog.readArticle')}
                                                            <ArrowRight className="w-5 h-5" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Category Filters */}
                            <div className="mb-8">
                                <div className="flex flex-wrap justify-center gap-2">
                                    {categories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${selectedCategory === category.id
                                                    ? 'bg-[#6547D9] text-white'
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#6547D9] hover:text-[#6547D9] hover:bg-[#f5f1ff]'
                                                }`}
                                        >
                                            {category.name}
                                            {category.count > 0 && (
                                                <span className="ml-2 opacity-70">({category.count})</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Enhanced Search Bar */}
                            <div className="mb-12 max-w-2xl mx-auto">
                                <BlogSearch
                                    posts={selectedCategory === 'all' ? posts : posts}
                                    onSearch={(filtered, term) => {
                                        setFilteredPosts(filtered);
                                        setSearchTerm(term || '');
                                    }}
                                />
                            </div>

                            {/* Articles Grid */}
                            {!searchTerm && regularPosts.length > 0 ? (
                                <div>
                                    <div className="flex items-center gap-4 mb-8">
                                        <h2 className="text-2xl font-bold text-gray-900">{t('blog.allArticles')}</h2>
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                        {regularPosts.map((post) => (
                                            <BlogCard key={post.slug} post={post} />
                                        ))}
                                    </div>
                                </div>
                            ) : searchTerm && filteredPosts.length > 0 ? (
                                <div>
                                    <div className="flex items-center gap-4 mb-8">
                                        <h2 className="text-2xl font-bold text-gray-900">{t('blog.searchResults')}</h2>
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                        {filteredPosts.map((post) => (
                                            <BlogCard key={post.slug} post={post} />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-16">
                                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                        {t('blog.noArticlesFound')}
                                    </h3>
                                    <p className="text-gray-500">
                                        {t('blog.tryModifyingSearchTerms')}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                {t('blog.noArticlesYet')}
                            </h3>
                            <p className="text-gray-500">
                                {t('blog.comeBackSoon')}
                            </p>
                        </div>
                    )}
                </div>

                <LandingFooter fr={i18n.language?.startsWith('fr') ?? false} />
            </main>
        </div>
    );
}
